import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { Button } from "@progress/kendo-react-buttons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";

import './TicketingWebsiteAlert.scss';

export const AdministratorTicketingWebsiteAlertVenues = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [ticketingWebsiteAlertVenues, setTicketingWebsiteAlertVenues] = useState([]);
    const [areTicketingWebsiteAlertVenuesLoading, setAreTicketingWebsiteAlertVenuesLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(null);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds
    
    const getTicketingWebsiteAlertVenues = () => {
        setAreTicketingWebsiteAlertVenuesLoading(true);

        const dataItem = {
            ticketingWebsiteAlertKey: props.ticketingWebsiteAlertKey,
        };
    
        fetch(`/api/administrator/ticketingwebsitealertvenues`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreTicketingWebsiteAlertVenuesLoading(false);
                setTicketingWebsiteAlertVenues(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreTicketingWebsiteAlertVenuesLoading(false);
                setLoadingError(error.message);
            });
    }

    const deleteTicketingWebsiteAlertVenue = (ticketingWebsiteAlertVenueKey) => {
        const dataItem = {
            "ticketingWebsiteAlertVenueKey": ticketingWebsiteAlertVenueKey
        }

        fetch("/api/administrator/ticketingwebsitealertvenue",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getTicketingWebsiteAlertVenues();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const saveVenue = () => {
        const venue = venues.find(element => element.currentName === venueName);

        if (!venue) {
            addNotificationWithTimeout(dispatch, "error", "You must select a venue");
            return;
        }

        const dataItem = {
            "ticketingWebsiteAlertKey": props.ticketingWebsiteAlertKey,
            "venueKey": venue.venueKey,
        }

        fetch("/api/administrator/ticketingwebsitealertvenue",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getTicketingWebsiteAlertVenues();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const discard = (dataItem) => {
        const newTicketingWebsiteAlertVenues = [...ticketingWebsiteAlertVenues];
        newTicketingWebsiteAlertVenues.splice(0, 1);
        setTicketingWebsiteAlertVenues(newTicketingWebsiteAlertVenues);
    };

    const remove = (dataItem) => {
        deleteTicketingWebsiteAlertVenue(dataItem.ticketingWebsiteAlertVenueKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="ticketingWebsiteAlertVenueKey"
        />
    );

    const itemChange = (event) => {
        const field = event.field || "";
        const newTicketingWebsiteAlertVenues = ticketingWebsiteAlertVenues.map((item) =>
            item.ticketingWebsiteAlertVenueKey === event.dataItem.ticketingWebsiteAlertVenueKey
                ? { ...item, [field]: event.value }
                : item
        );
        setTicketingWebsiteAlertVenues(newTicketingWebsiteAlertVenues);
    };

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);  
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    useEffect(() => {
        if (props.ticketingWebsiteAlertKey) {
            getTicketingWebsiteAlertVenues();
        }
    }, []);

    return (
        <div>
            <div className="administratorTicketingWebsiteAlertVenueAdd">
                <Label>Add venue:</Label> 

                <AutoComplete
                    id={"venue"}
                    name={"venue"}
                    data={venues}        
                    placeholder="e.g. Roseland"
                    textField="currentName"
                    onChange={venueChanged}
                    loading={areVenuesLoading}
                />

                <Button
                    themeColor={"primary"}
                    type={"button"}
                    onClick={saveVenue}
                    >
                    Save
                </Button>
            </div>
            {areTicketingWebsiteAlertVenuesLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <Grid
                    data={ticketingWebsiteAlertVenues}
                    onItemChange={itemChange}
                    editField={editField}
                    dataItemKey={"ticketingWebsiteAlertVenueKey"}
                    resizable={true}
                    scrollable="none"
                >
                    <Column field="venueName" title={"Venue"} />
                    <Column cell={MyCommandCell} width="150px" />
                </Grid>
            ) : <></>)
            }
        </div>
    );
}